import { api } from '@/api/client';
import { isKringSystem } from '@/stores/systems';

// A systeem code as stored in Odoo. NOT a static union: the roster lives in the Odoo
// selection fields and reaches the frontend through /api/systems (stores/systems), so
// a system added in Odoo is valid here without a deploy.
export type CadSysteemEnum = string;

/**
 * Map a CAD export's `Systeem` label to a portal systeem code. Kept behaviour-identical
 * to the server copy in portal-api/src/utils/cad-json.ts — the branches are CAD spelling
 * knowledge ("Staalnet gebonden", "Slimfit*" → warp, "Renothin20/30"), not a roster. A
 * label that IS a roster code maps to itself, so a newly added system auto-maps once its
 * CAD label matches (TD-095). Anything else → 'meerdere systemen', which is invalid as a
 * kring value on purpose: the wizard then forces a manual pick (ADR-061 §3).
 */
export function mapCadSysteemType(value: string): CadSysteemEnum {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'tacker') return 'tacker';
  if (normalized === 'staalnet' || normalized === 'staalnet gebonden') return 'staalnet';
  if (normalized === 'noppenfolie') return 'noppenfolie';
  if (normalized === 'betonnet') return 'betonnet';
  if (normalized === 'infrezen') return 'infrezen';
  if (normalized === 'warp') return 'warp';
  // Slimfit is the CAD export's label for the warp system (Slimfit12/16/… → warp).
  if (normalized.startsWith('slimfit')) return 'warp';
  // Renothin 20 / 30 are DISTINCT systems — map by number; bare "Renothin" → manual-pick.
  if (normalized.startsWith('renothin')) {
    if (normalized.includes('30')) return 'renothin30';
    if (normalized.includes('20')) return 'renothin20';
  }
  // Ultra-thin renovation system ('Ultra-thin' / 'ultrathin' / …).
  if (normalized.replace(/[^a-z]/g, '').startsWith('ultrathin')) return 'ultrathin';
  // A label that IS a roster code (possibly spaced/hyphenated) maps to itself.
  if (isKringSystem(normalized)) return normalized;
  const collapsed = normalized.replace(/[^a-z0-9]/g, '');
  if (isKringSystem(collapsed)) return collapsed;
  return 'meerdere systemen';
}

export interface CadJsonFile {
  name: string;
  path: string;
  lastModified?: string;
  sizeBytes?: number;
}

export interface CadMateriaalLine {
  artikelnummer: string;
  uploadArtikelnummer?: string;
  omschrijving: string;
  aantal: number;
  sectie: string;
  isHandmatig: boolean;
  productFound?: boolean;
}

export interface CadCollectorLine {
  collNr: string;
  niveau: string;
  m2: number;
  appartement: string;
  kringen: Array<{
    kringNr: number;
    lengthM: number;
    legpatroon: string;
    rolType: string;
    kleur: string;
    // Raw per-kring system from the CAD export ('' when omitted). Mapped per kring via
    // mapCadSysteemType in the import wizard, falling back to instellingen.systeemType.
    systeem: string;
  }>;
}

export interface CadJsonPayload {
  snummer: string;
  datumTijd: string;
  fileName: string;
  instellingen: {
    collectorType: string;
    systeemType: string;
    legpatroon: string;
    buisType: string;
    zoneOppervlakte: string;
    aantalHulzen: string;
    opmerkingen: string;
  };
  materiaal: CadMateriaalLine[];
  collectoren: CadCollectorLine[];
  warning?: {
    code: 'SO_MISMATCH';
    expected: string;
    found: string;
    message: string;
  };
}

export async function parseCadJsonFile(projectId: number, path: string): Promise<CadJsonPayload> {
  return api<CadJsonPayload>(`/api/projects/${projectId}/cad-json/parse?path=${encodeURIComponent(path)}`);
}
